import React, { useState, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CommonImageModal from "components/commonImageModal/CommonImageModal";
import CommonButton from "components/commonButton/CommonButton";
import { truncateComment } from "utils/utils";
import { toast } from "react-toastify";
import requestInstance from "services/Request";
import { API_ENDPOINT } from "services/Api";
import "./ReservationAttachments.scss";

const env = process.env.REACT_APP_B2B_API_BASE_URL;

const ReservationAttachmentsDialog = ({ open, handleClose, attachments, tripId, onSave, readOnly }) => {
  const [tollList, setTollList] = useState([...(attachments?.tolls || [])]);
  const [parkingList, setParkingList] = useState([...(attachments?.parking || [])]);
  const [previewModal, setPreviewModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [reuploadInfo, setReuploadInfo] = useState(null); // { type, index }
  const [addType, setAddType] = useState(null); // 'toll' | 'parking'
  const [removedTolls, setRemovedTolls] = useState([]); // s3 keys to remove
  const [removedParking, setRemovedParking] = useState([]); // s3 keys to remove
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const addFileInputRef = useRef(null);

  const handlePreview = (url) => {
    if (url) {
      setSelectedImage(url);
      setPreviewModal(true);
    }
  };

  const handlePreviewClose = () => {
    setSelectedImage(null);
    setPreviewModal(false);
  };

  // ---- Remove ----
  const handleRemove = (type, index) => {
    if (type === "toll") {
      const item = tollList[index];
      // Track removal of existing (non-new) items by their s3 key
      if (item?.key && !item?.isNew) {
        setRemovedTolls((prev) => [...prev, item.key]);
      }
      setTollList((prev) => prev.filter((_, i) => i !== index));
    } else {
      const item = parkingList[index];
      if (item?.key && !item?.isNew) {
        setRemovedParking((prev) => [...prev, item.key]);
      }
      setParkingList((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // ---- Reupload ----
  const handleReuploadClick = (e, type, index) => {
    e.stopPropagation();
    setReuploadInfo({ type, index });
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !reuploadInfo) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size should not exceed 5MB");
      return;
    }
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG images are allowed");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const newItem = { url: previewUrl, name: file.name, file, isNew: true };

    if (reuploadInfo.type === "toll") {
      const oldItem = tollList[reuploadInfo.index];
      if (oldItem?.key && !oldItem?.isNew) {
        setRemovedTolls((prev) => [...prev, oldItem.key]);
      }
      setTollList((prev) => prev.map((item, i) => (i === reuploadInfo.index ? newItem : item)));
    } else {
      const oldItem = parkingList[reuploadInfo.index];
      if (oldItem?.key && !oldItem?.isNew) {
        setRemovedParking((prev) => [...prev, oldItem.key]);
      }
      setParkingList((prev) => prev.map((item, i) => (i === reuploadInfo.index ? newItem : item)));
    }

    setReuploadInfo(null);
    e.target.value = "";
  };

  // ---- Add New ----
  const handleAddNew = (type) => {
    setAddType(type);
    addFileInputRef.current?.click();
  };

  const handleAddFileChange = (e) => {
    const file = e.target.files[0];
    if (!file || !addType) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size should not exceed 5MB");
      return;
    }
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG images are allowed");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const newItem = { url: previewUrl, name: file.name, file, isNew: true };

    if (addType === "toll") {
      setTollList((prev) => [...prev, newItem]);
    } else {
      setParkingList((prev) => [...prev, newItem]);
    }

    setAddType(null);
    e.target.value = "";
  };

  // ---- Save ----
  const handleSave = async () => {
    const newTolls = tollList.filter((item) => item?.isNew && item?.file);
    const newParking = parkingList.filter((item) => item?.isNew && item?.file);
    const hasNewFiles = newTolls.length > 0 || newParking.length > 0;
    const hasRemovals = removedTolls.length > 0 || removedParking.length > 0;

    if (!hasNewFiles && !hasRemovals) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("tripId", tripId);

      // Append new toll files
      newTolls.forEach((item) => {
        formData.append("attachments_tolls", item.file);
      });

      // Append new parking files
      newParking.forEach((item) => {
        formData.append("attachments_parking", item.file);
      });

      // Append removed keys
      if (hasRemovals) {
        const removeAttachments = {};
        if (removedTolls.length > 0) removeAttachments.tolls = removedTolls;
        if (removedParking.length > 0) removeAttachments.parking = removedParking;
        formData.append("removeAttachments", JSON.stringify(removeAttachments));
      }

      await requestInstance.post(
        `${env}${API_ENDPOINT.reservation.uploadTripAttachmentsApi}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success("Attachments saved successfully");
      setRemovedTolls([]);
      setRemovedParking([]);
      if (onSave) onSave();
      handleClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save attachments");
    } finally {
      setSaving(false);
    }
  };

  // ---- Render Card ----
  const renderCard = (item, index, type) => {
    const url = item?.url || "";

    return (
      <Grid
        item
        md={3.7}
        sm={5.8}
        xs={12}
        className="reservationAttachmentCard borderStyle padding10 m10"
        key={`${type}-${index}`}
        onClick={() => handlePreview(url)}
      >
        {/* Image Preview */}
        <div className="dlImgMain">
          <div className="dlImgBg"></div>
          <div className="attachmentPreviewLabel">
            {url ? (
              <img
                src={url}
                alt={`${type} ${index + 1}`}
                className="dlImg"
              />
            ) : (
              <Box className="noPreview flex justifyCenter alignCenter">
                <Typography variant="body3" className="colorGrey">
                  No Preview
                </Typography>
              </Box>
            )}
          </div>
        </div>

        {/* Card Footer - Name + Actions */}
        <Grid container justifyContent="space-between" alignItems="center" className="padding10">
          <Grid item xs={7}>
            <Tooltip title={item?.name || ""} placement="bottom">
              <Typography variant="body3" className="colorBlack fw600">
                {truncateComment(item?.name || `${type} ${index + 1}`, 20)}
              </Typography>
            </Tooltip>
            {item?.isNew && (
              <Typography variant="body5" className="newBadge">New</Typography>
            )}
          </Grid>

          <Grid item xs={5} container justifyContent="flex-end" alignItems="center" spacing={1}>
            {/* Reupload */}
            <Grid item>
              <Tooltip title="Re-upload" placement="top">
                <CloudSyncOutlinedIcon
                  onClick={(e) => !readOnly && handleReuploadClick(e, type, index)}
                  className="reuploadIcon"
                  sx={{ opacity: readOnly ? 0.4 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                />
              </Tooltip>
            </Grid>

            {/* Delete */}
            <Grid item>
              <Tooltip title="Remove" placement="top">
                <DeleteOutlineIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!readOnly) handleRemove(type, index);
                  }}
                  className="deleteIcon"
                  sx={{ opacity: readOnly ? 0.4 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  // ---- Section ----
  const renderSection = (title, items, type) => {
    if (!items || items.length === 0) {
      return (
        <Box className="mb20">
          <Typography variant="head6" className="fw600 colorBlack mb10">
            {title}
          </Typography>
          <Grid container direction="row" justifyContent="flex-start" alignItems="start">
            <Grid
              item
              md={3.7}
              sm={5.8}
              xs={12}
              className="addNewCard borderStyle padding10 m10"
              onClick={() => !readOnly && handleAddNew(type)}
              sx={{ opacity: readOnly ? 0.4 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
            >
              <Box className="addNewCardInner">
                <AddPhotoAlternateOutlinedIcon className="addNewIcon" />
                <Typography variant="body2" className="colorGrey mt10">
                  Upload New Image
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }
    return (
      <Box className="mb20">
        <Typography variant="head6" className="fw600 colorBlack mb10">
          {title} ({items.length})
        </Typography>
        <Grid container direction="row" justifyContent="flex-start" alignItems="start">
          {items.map((item, idx) => renderCard(item, idx, type))}

          {/* Add New Card */}
          <Grid
            item
            md={3.7}
            sm={5.8}
            xs={12}
            className="addNewCard borderStyle padding10 m10"
            onClick={() => !readOnly && handleAddNew(type)}
            sx={{ opacity: readOnly ? 0.4 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }}
          >
            <Box className="addNewCardInner">
              <AddPhotoAlternateOutlinedIcon className="addNewIcon" />
              <Typography variant="body2" className="colorGrey mt10">
                Upload New Image
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        aria-labelledby="reservation-attachments-dialog"
      >
        <DialogTitle className="flex justifyBetween alignCenter">
          <Typography variant="head5" className="fw600 colorBlack">
            Toll & Parking Attachments
          </Typography>
          <IconButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Hidden file inputs */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png"
            style={{ display: "none" }}
          />
          <input
            type="file"
            ref={addFileInputRef}
            onChange={handleAddFileChange}
            accept=".jpg,.jpeg,.png"
            style={{ display: "none" }}
          />

          {renderSection("Toll Attachments", tollList, "toll")}
          <div className="dashedborder mb20"></div>
          {renderSection("Parking Attachments", parkingList, "parking")}

          {/* Save Button */}
          <Grid container justifyContent="flex-end" className="mt20">
            <CommonButton
              name={saving ? "Updating..." : "Update"}
              btnClass="btnSm"
              onClick={handleSave}
              disabled={saving || readOnly}
            />
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Image Preview */}
      {previewModal && (
        <CommonImageModal
          open={previewModal}
          handleClose={handlePreviewClose}
          selectedImage={selectedImage}
        />
      )}
    </>
  );
};

export default ReservationAttachmentsDialog;
